import React, { useState, useMemo } from 'react';
import { Icons } from '../../utils/icons';
import { authService } from '../../services/authService';
import { expandirRecurrencia } from './utils/recurrencia';

function hoy() { return new Date().toISOString().split('T')[0]; }
function addDias(fecha, n) {
  const d = new Date(fecha + 'T12:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().split('T')[0];
}
function fmt(fecha) {
  if (!fecha) return '';
  const [y, m, d] = fecha.split('-');
  return `${d}/${m}/${y}`;
}

const GRUPOS = [
  { id: 'vencidas',    label: 'Vencidas' },
  { id: 'hoy',         label: 'Hoy' },
  { id: 'proximos',    label: 'Próximos 7 días' },
  { id: 'masAdelante', label: 'Más adelante' },
  { id: 'sinFecha',    label: 'Sin fecha' },
];

function clasificar(fechaVencimiento) {
  if (!fechaVencimiento) return 'sinFecha';
  const h = hoy();
  if (fechaVencimiento < h) return 'vencidas';
  if (fechaVencimiento === h) return 'hoy';
  if (fechaVencimiento <= addDias(h, 7)) return 'proximos';
  return 'masAdelante';
}

export default function TareasView({ raw, usuario, onEditar, onNuevaTarea }) {
  const [filtroTag, setFiltroTag] = useState(null);
  const [mostrarCompletadas, setMostrarCompletadas] = useState(false);
  const [marcando, setMarcando] = useState(null);

  const ocurrencias = useMemo(() => {
    // Solo tareas propias: creadas por mí o donde me han añadido como
    // participante. No se listan las de otras personas (privacidad).
    const email = usuario?.email?.toLowerCase();
    const maestros = raw.filter(e =>
      !e.seriePadreId && e.tipo === 'tarea' &&
      (e.creadorId?.toLowerCase() === email || (e.participantes || []).some(p => p.userId?.toLowerCase() === email))
    );
    const excepcionesPorMaestro = new Map();
    for (const e of raw.filter(e => e.seriePadreId)) {
      if (!excepcionesPorMaestro.has(e.seriePadreId)) excepcionesPorMaestro.set(e.seriePadreId, []);
      excepcionesPorMaestro.get(e.seriePadreId).push(e);
    }
    // Ventana amplia (90 días atrás, 180 adelante) para no perder tareas
    // vencidas antiguas ni futuras lejanas de series recurrentes.
    const rangoInicio = addDias(hoy(), -90);
    const rangoFin = addDias(hoy(), 180);
    const out = [];
    for (const m of maestros) {
      const excs = excepcionesPorMaestro.get(m.id) || [];
      out.push(...expandirRecurrencia(m, excs, rangoInicio, rangoFin));
    }
    return out;
  }, [raw]);

  const todosTags = useMemo(() => {
    const set = new Set();
    for (const oc of ocurrencias) for (const t of (oc.tags || [])) set.add(t);
    return [...set].sort();
  }, [ocurrencias]);

  const filtradas = useMemo(() =>
    filtroTag ? ocurrencias.filter(oc => (oc.tags || []).includes(filtroTag)) : ocurrencias,
  [ocurrencias, filtroTag]);

  const pendientes = filtradas.filter(oc => !oc.completada);
  const completadas = filtradas.filter(oc => oc.completada);

  const grupos = useMemo(() => {
    const map = { vencidas: [], hoy: [], proximos: [], masAdelante: [], sinFecha: [] };
    for (const oc of pendientes) map[clasificar(oc.fechaVencimiento || oc.fechaInicio)].push(oc);
    for (const g of Object.values(map)) g.sort((a, b) => (a.fechaVencimiento || '').localeCompare(b.fechaVencimiento || ''));
    return map;
  }, [pendientes]);

  const toggleCompletada = async (oc) => {
    const esOcurrenciaDeSerie = !!(oc.recurrencia && oc.recurrencia.frecuencia !== 'ninguna');
    const key = `${oc.eventoId}-${oc.fechaOriginal}`;
    setMarcando(key);
    try {
      await fetch('/.netlify/functions/marcarTareaCompletada', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({
          eventoId: oc.eventoId,
          fechaOriginal: esOcurrenciaDeSerie ? oc.fechaOriginal : undefined,
          completada: !oc.completada,
        }),
      });
    } catch (e) { console.error('[marcarTareaCompletada]', e.message); }
    finally { setMarcando(null); }
  };

  function Fila({ oc }) {
    const key = `${oc.eventoId}-${oc.fechaOriginal}`;
    const vencida = !oc.completada && oc.fechaVencimiento && oc.fechaVencimiento < hoy();
    return (
      <div className="flex items-start gap-3 px-3 py-2.5 hover:bg-gray-50 rounded-xl">
        <button onClick={() => toggleCompletada(oc)} disabled={marcando === key}
          className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors disabled:opacity-50 ${oc.completada ? 'bg-red-600 border-red-600' : 'border-gray-300 hover:border-red-400'}`}>
          {oc.completada && <Icons.Check className="w-3 h-3 text-white" />}
        </button>
        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onEditar(oc)}>
          <p className={`text-sm font-medium ${oc.completada ? 'text-gray-400 line-through' : 'text-gray-800'}`}>{oc.titulo}</p>
          <div className="flex items-center gap-2 flex-wrap mt-0.5">
            {oc.fechaVencimiento && (
              <span className={`text-xs ${vencida ? 'text-red-600 font-bold' : 'text-gray-400'}`}>
                {fmt(oc.fechaVencimiento)}{oc.horaInicio ? ` · ${oc.horaInicio}` : ''}
              </span>
            )}
            {(oc.tags || []).map(t => (
              <span key={t} className="text-xs font-bold text-indigo-700 bg-indigo-50 px-1.5 py-0.5 rounded-full">#{t}</span>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg border p-4 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex flex-wrap gap-1.5">
          <button onClick={() => setFiltroTag(null)}
            className={`px-3 py-1.5 text-xs font-bold rounded-full border transition-colors ${!filtroTag ? 'bg-red-600 text-white border-red-600' : 'bg-white text-gray-500 border-gray-200'}`}>
            Todas
          </button>
          {todosTags.map(t => (
            <button key={t} onClick={() => setFiltroTag(t === filtroTag ? null : t)}
              className={`px-3 py-1.5 text-xs font-bold rounded-full border transition-colors ${filtroTag === t ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-500 border-gray-200'}`}>
              #{t}
            </button>
          ))}
        </div>
        <button onClick={onNuevaTarea}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-bold text-white bg-red-600 hover:bg-red-700 rounded-xl transition-colors">
          <Icons.Plus className="w-4 h-4" />Nueva tarea
        </button>
      </div>

      {pendientes.length === 0 && completadas.length === 0 && (
        <p className="text-sm text-gray-400 text-center py-10">Sin tareas{filtroTag ? ` con #${filtroTag}` : ''}.</p>
      )}

      {GRUPOS.map(g => grupos[g.id].length > 0 && (
        <div key={g.id}>
          <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1 px-3">{g.label} ({grupos[g.id].length})</p>
          <div>{grupos[g.id].map(oc => <Fila key={`${oc.eventoId}-${oc.fechaOriginal}`} oc={oc} />)}</div>
        </div>
      ))}

      {completadas.length > 0 && (
        <div>
          <button onClick={() => setMostrarCompletadas(s => !s)}
            className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1 px-3 flex items-center gap-1">
            <Icons.ChevronRight className={`w-3 h-3 transition-transform ${mostrarCompletadas ? 'rotate-90' : ''}`} />
            Completadas ({completadas.length})
          </button>
          {mostrarCompletadas && (
            <div>{completadas.map(oc => <Fila key={`${oc.eventoId}-${oc.fechaOriginal}`} oc={oc} />)}</div>
          )}
        </div>
      )}
    </div>
  );
}
